import { apiService } from './api';
import { ApiResponse } from '../types';

export interface Questionnaire {
  id?: number; // Numeric ID for API calls
  label: string; // Renamed from questionnaireId - this is the display label
  version: number;
  name: string;
  description?: string;
  isActive: boolean;
  createdBy: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  questionCount?: number;
  assessmentCount?: number;
  /** Present on rows returned from GET /questionnaires/:id/versions (per-version assessments). */
  assessments?: Array<{
    id: number;
    clientId: number;
    clientName?: string;
    status: string;
    createdAt: string;
  }>;
  // Legacy field for backward compatibility - maps to label
  questionnaireId?: string;
}

export interface QuestionnaireVersion {
  id: number; // Numeric ID - REQUIRED for API calls
  label: string; // Renamed from questionnaireId
  questionnaireId: string; // Legacy field - maps to label
  /** Sum of assessments across all versions (evaluations using this template). */
  totalAssessmentCount?: number;
  /** True if any version row is archived. */
  isArchived?: boolean;
  versions: Array<{
    id: number; // Numeric ID - REQUIRED for API calls
    label: string; // Renamed from questionnaireId
    version: number;
    name: string;
    description?: string;
    isActive: boolean;
    isArchived?: boolean;
    createdBy: {
      id: number;
      firstName: string;
      lastName: string;
      email: string;
    };
    createdAt: string;
    updatedAt: string;
    questionCount: number;
    assessmentCount: number;
    assessments?: Array<{
      id: number;
      clientId: number;
      clientName?: string;
      advisorName: string;
      status: string;
      createdAt: string;
    }>;
  }>;
}

export interface QuestionnaireWithQuestions extends Questionnaire {
  questions: Array<{
    id: number;
    label: string; // Renamed from questionId
    dimensionId: number;
    questionnaireLabel?: string; // Renamed from questionnaireId
    questionnaireVersion?: number;
    tags: string[];
    dependencies?: Record<string, any>;
    questionText: string;
    questionType: string;
    scoringRules?: Record<string, number>;
    scoringRuleEntries?: Array<{ answerValue: string; score: number }>;
    weighting?: number;
    displayOrder?: number | null;
    isAiQuestion: boolean;
    isActive: boolean;
    isParent?: boolean;
    isChild?: boolean;
    dimension?: {
      id: number;
      name: string;
      label: string;
    };
    options?: Array<{
      id: number;
      questionLabel: string; // Renamed from questionId
      optionValue: string;
      optionText?: string;
      displayOrder: number;
      isActive: boolean;
    }>;
    parentBranches?: Array<{
      id: number;
      parentQuestionLabel: string; // Renamed from parentQuestionId
      parentOptionValue: string;
      childQuestionLabel: string; // Renamed from childQuestionId
      conditionType: string;
      isActive: boolean;
      childQuestion?: {
        label: string; // Renamed from questionId
        questionText: string;
        questionType: string;
        isParent: boolean;
        isChild: boolean;
      };
      triggeringOption?: {
        optionValue: string;
        optionText?: string;
      };
    }>;
    childBranches?: Array<{
      id: number;
      parentQuestionLabel: string; // Renamed from parentQuestionId
      parentOptionValue: string;
      childQuestionLabel: string; // Renamed from childQuestionId
      conditionType: string;
      isActive: boolean;
      parentQuestion?: {
        label: string; // Renamed from questionId
        questionText: string;
        questionType: string;
      };
      triggeringOption?: {
        optionValue: string;
        optionText?: string;
      };
    }>;
    parentGroup?: {
      id: number;
      parentQuestionLabel: string; // Renamed from parentQuestionId
      isActive: boolean;
      members?: Array<{
        id: number;
        groupId: number;
        questionLabel: string; // Renamed from questionId
        isActive: boolean;
        question?: {
          label: string; // Renamed from questionId
          questionText: string;
          questionType: string;
          weighting?: number;
          isParent: boolean;
          isChild: boolean;
        };
      }>;
    };
    groupMemberships?: Array<{
      id: number;
      groupId: number;
      questionLabel: string; // Renamed from questionId
      isActive: boolean;
      group?: {
        id: number;
        parentQuestionLabel: string; // Renamed from parentQuestionId
        isActive: boolean;
        parentQuestion?: {
          label: string; // Renamed from questionId
          questionText: string;
        };
      };
    }>;
    // Legacy fields for backward compatibility
    questionId?: string;
    questionnaireId?: string;
  }>;
}

class QuestionnaireService {
  async getQuestionnaires(): Promise<QuestionnaireVersion[]> {
    const response = await apiService.get<ApiResponse<{ questionnaires: QuestionnaireVersion[] }>>('/questionnaires');
    if (response.data?.questionnaires) {
      return response.data.questionnaires;
    }
    throw new Error(response.message || 'Failed to fetch questionnaires');
  }

  // Get questionnaire by ID (numeric) or by label (if useLabel=true)
  async getQuestionnaire(questionnaireIdOrLabel: string | number, version?: number, useLabel: boolean = false): Promise<QuestionnaireWithQuestions> {
    const idParam = typeof questionnaireIdOrLabel === 'number' ? questionnaireIdOrLabel : questionnaireIdOrLabel;
    const url = version
      ? `/questionnaires/${idParam}?version=${version}${useLabel ? '&label=true' : ''}`
      : `/questionnaires/${idParam}${useLabel ? '?label=true' : ''}`;
    const response = await apiService.get<ApiResponse<{ questionnaire: QuestionnaireWithQuestions }>>(url);
    if (response.data?.questionnaire) {
      return response.data.questionnaire;
    }
    throw new Error(response.message || 'Failed to fetch questionnaire');
  }

  // Get questionnaire versions by ID (numeric) or by label (if useLabel=true)
  async getQuestionnaireVersions(questionnaireIdOrLabel: string | number, useLabel: boolean = false): Promise<Questionnaire[]> {
    const idParam = typeof questionnaireIdOrLabel === 'number' ? questionnaireIdOrLabel : questionnaireIdOrLabel;
    const url = `/questionnaires/${idParam}/versions${useLabel ? '?label=true' : ''}`;
    const response = await apiService.get<ApiResponse<{ versions: Questionnaire[] }>>(url);
    if (response.data?.versions) {
      return response.data.versions;
    }
    throw new Error(response.message || 'Failed to fetch questionnaire versions');
  }

  async createQuestionnaire(data: {
    questionnaireId: string; // This becomes the label
    name: string;
    description?: string;
  }): Promise<Questionnaire> {
    const response = await apiService.post<ApiResponse<{ questionnaire: Questionnaire }>>('/questionnaires', data);
    if (response.data?.questionnaire) {
      return response.data.questionnaire;
    }
    throw new Error(response.message || 'Failed to create questionnaire');
  }

  // Update questionnaire by ID (numeric) or by label (if useLabel=true)
  async updateQuestionnaire(questionnaireIdOrLabel: string | number, data: {
    name?: string;
    description?: string;
    questions?: any[];
  }, useLabel: boolean = false): Promise<Questionnaire> {
    const idParam = typeof questionnaireIdOrLabel === 'number' ? questionnaireIdOrLabel : questionnaireIdOrLabel;
    const url = `/questionnaires/${idParam}${useLabel ? '?label=true' : ''}`;
    const response = await apiService.put<ApiResponse<{ questionnaire: Questionnaire }>>(url, data);
    if (response.data?.questionnaire) {
      return response.data.questionnaire;
    }
    throw new Error(response.message || 'Failed to update questionnaire');
  }

  // Duplicate questionnaire by ID (numeric) or by label (if useLabel=true)
  async duplicateQuestionnaire(questionnaireIdOrLabel: string | number, data: {
    newQuestionnaireId: string; // This becomes the new label
    name: string;
    description?: string;
  }, useLabel: boolean = false): Promise<Questionnaire> {
    const idParam = typeof questionnaireIdOrLabel === 'number' ? questionnaireIdOrLabel : questionnaireIdOrLabel;
    const url = `/questionnaires/${idParam}/duplicate${useLabel ? '?label=true' : ''}`;
    const response = await apiService.post<ApiResponse<{ questionnaire: Questionnaire }>>(url, data);
    if (response.data?.questionnaire) {
      return response.data.questionnaire;
    }
    throw new Error(response.message || 'Failed to duplicate questionnaire');
  }

  // Activate version by ID (numeric) or by label (if useLabel=true)
  async activateVersion(questionnaireIdOrLabel: string | number, version: number, useLabel: boolean = false): Promise<Questionnaire> {
    const idParam = typeof questionnaireIdOrLabel === 'number' ? questionnaireIdOrLabel : questionnaireIdOrLabel;
    const url = `/questionnaires/${idParam}/versions/${version}/activate${useLabel ? '?label=true' : ''}`;
    const response = await apiService.post<ApiResponse<{ questionnaire: Questionnaire }>>(url);
    if (response.data?.questionnaire) {
      return response.data.questionnaire;
    }
    throw new Error(response.message || 'Failed to activate version');
  }

  // Get questions by questionnaire ID (numeric) or by label (if useLabel=true)
  async getQuestionnaireQuestions(questionnaireIdOrLabel: string | number, version?: number, useLabel: boolean = false): Promise<any[]> {
    const idParam = typeof questionnaireIdOrLabel === 'number' ? questionnaireIdOrLabel : questionnaireIdOrLabel;
    const url = version
      ? `/questionnaires/${idParam}/questions?version=${version}${useLabel ? '&label=true' : ''}`
      : `/questionnaires/${idParam}/questions${useLabel ? '?label=true' : ''}`;
    const response = await apiService.get<ApiResponse<{ questions: any[] }>>(url);
    if (response.data?.questions) {
      return response.data.questions;
    }
    throw new Error(response.message || 'Failed to fetch questions');
  }

  // Add question by questionnaire ID (numeric) or by label (if useLabel=true)
  async addQuestion(questionnaireIdOrLabel: string | number, questionData: any, useLabel: boolean = false): Promise<any> {
    const idParam = typeof questionnaireIdOrLabel === 'number' ? questionnaireIdOrLabel : questionnaireIdOrLabel;
    const url = `/questionnaires/${idParam}/questions${useLabel ? '?label=true' : ''}`;
    const response = await apiService.post<ApiResponse<{ question: any }>>(url, questionData);
    if (response.data?.question) {
      return response.data.question;
    }
    throw new Error(response.message || 'Failed to add question');
  }

  // Update question - questionnaireId should be numeric ID, questionId can be numeric ID or label
  async updateQuestion(questionnaireId: number, questionIdOrLabel: string | number, updateData: any): Promise<any> {
    const questionParam = typeof questionIdOrLabel === 'number' ? questionIdOrLabel : questionIdOrLabel;
    const response = await apiService.put<ApiResponse<{ question: any }>>(
      `/questionnaires/${questionnaireId}/questions/${questionParam}`,
      updateData
    );
    if (response.data?.question) {
      return response.data.question;
    }
    throw new Error(response.message || 'Failed to update question');
  }

  // Remove question - questionnaireId should be numeric ID, questionId can be numeric ID or label
  async removeQuestion(questionnaireId: number, questionIdOrLabel: string | number): Promise<void> {
    const questionParam = typeof questionIdOrLabel === 'number' ? questionIdOrLabel : questionIdOrLabel;
    await apiService.delete(`/questionnaires/${questionnaireId}/questions/${questionParam}`);
  }

  // Reorder questions - questionnaireId should be numeric ID, payload can contain questionIds or dimensions array
  async reorderQuestions(
    questionnaireId: number,
    payload: {
      questionIds?: (string | number)[];
      dimensions?: Array<{ dimensionId: number; questionIds: (string | number)[] }>;
    }
  ): Promise<void> {
    await apiService.post<ApiResponse<void>>(
      `/questionnaires/${questionnaireId}/questions/reorder`,
      payload
    );
  }

  // Delete version by ID (numeric) or by label (if useLabel=true)
  async deleteVersion(questionnaireIdOrLabel: string | number, version: number, useLabel: boolean = false): Promise<void> {
    const idParam = typeof questionnaireIdOrLabel === 'number' ? questionnaireIdOrLabel : questionnaireIdOrLabel;
    const url = `/questionnaires/${idParam}/versions/${version}${useLabel ? '?label=true' : ''}`;
    await apiService.delete<ApiResponse<void>>(url);
  }

  /** SuperAdmin: archive all versions of a template (stops new evaluations from using it). */
  async archiveQuestionnaireTemplate(label: string): Promise<void> {
    await apiService.post(
      `/questionnaires/label/${encodeURIComponent(label)}/archive`,
      {}
    );
  }

  /** SuperAdmin: delete entire template when no assessments reference it. */
  async deleteQuestionnaireTemplate(label: string): Promise<void> {
    await apiService.delete(`/questionnaires/label/${encodeURIComponent(label)}`);
  }

  /** SuperAdmin: create a template from canonical seed data (same source as the DB seed script). */
  async createQuestionnaireFromSeed(data: {
    newQuestionnaireId: string;
    name: string;
    description?: string;
  }): Promise<Questionnaire> {
    const response = await apiService.post<ApiResponse<{ questionnaire: Questionnaire }>>(
      '/questionnaires/actions/create-from-seed',
      data
    );
    if (response.data?.questionnaire) {
      return response.data.questionnaire;
    }
    throw new Error(response.message || 'Failed to create questionnaire from seed');
  }

  /** SuperAdmin: download questionnaire template as CSV (any row id for that template; optional version). */
  async exportQuestionnaireCsv(questionnaireRowId: number, version?: number): Promise<Blob> {
    const q =
      version !== undefined && !Number.isNaN(version)
        ? `?version=${version}`
        : '';
    return apiService.getBlob(`/questionnaires/${questionnaireRowId}/export-csv${q}`);
  }

  /** SuperAdmin: import template from CSV export. */
  async importQuestionnaireFromCsv(data: {
    file: File;
    newQuestionnaireId: string;
    name: string;
    description?: string;
  }): Promise<Questionnaire> {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('newQuestionnaireId', data.newQuestionnaireId);
    formData.append('name', data.name);
    if (data.description != null && data.description !== '') {
      formData.append('description', data.description);
    }
    const response = await apiService.postFormData<ApiResponse<{ questionnaire: Questionnaire }>>(
      '/questionnaires/actions/import-csv',
      formData
    );
    if (response.data?.questionnaire) {
      return response.data.questionnaire;
    }
    throw new Error(response.message || 'Failed to import questionnaire from CSV');
  }
}

export const questionnaireService = new QuestionnaireService();

